// ChallengeConfig.js
// Local configuration for the 21-Day Challenge feature.
//
// Phase 1 (UI-only): these values are hardcoded on the frontend because the
// backend `Challenge` program-template model (see design doc
// docs/plans/2026-07-05-21-day-challenge-design.md §4.3) does not exist yet.
// Phase 2+ should replace CHALLENGE_PRICING / CHALLENGE_RULES with a call to
// `GET /challenge/config` and keep this file only as a fallback/default.

/**
 * Deposit pricing by locale. India pays in INR, everyone else pays in USD.
 * Refund model is all-or-nothing: complete the challenge and the full
 * deposit is refunded; fail to complete it and the deposit is forfeited
 * (no partial/milestone refunds — avoids per-partial-refund payment
 * gateway fees).
 */
export const CHALLENGE_PRICING = {
  IN: { amount: 1000, currency: "INR", symbol: "₹" },
  INTL: { amount: 20, currency: "USD", symbol: "$" },
};

/**
 * The program's name, as it appears in headings and on the achievement
 * badge. Matches CHALLENGE_TITLE in the backend's challenge endpoints —
 * there is exactly one program, so a shared literal is honest and the day
 * count cannot drift away from CHALLENGE_RULES.TOTAL_DAYS below.
 */
export const CHALLENGE_TITLE = "21-Day Speaking Challenge";

export const CHALLENGE_RULES = {
  TOTAL_DAYS: 21,
  /** Practice days required to reach LESSONS_COMPLETED. All of them. */
  COMPLETION_MIN_DAYS: 21,
  /**
   * Missed days that can be covered without breaking the streak. Zero under
   * the all-or-nothing rule: a frozen day counts as valid, so any freeze
   * would be a hole in "all 21 days".
   */
  FREEZES_GRANTED: 0,
  /**
   * Days after all 21 lessons are submitted during which the (optional)
   * final assessment can still be taken. Refund is issued regardless of
   * whether the final assessment happens — it is for the user's own
   * improvement tracking only, per product decision 2026-08-15.
   */
  FINAL_ASSESSMENT_GRACE_DAYS: 7,
  /**
   * Days the completion screen stays up after the final lesson day before
   * the user is offered a fresh challenge. The refund takes 5-7 working
   * days to land, so moving them on any sooner would retire the screen that
   * tells them it is coming (product decision, MR !91 review).
   */
  POST_COMPLETION_DAYS: 15,
};

export const CHALLENGE_STATUS = {
  NONE: "NONE",
  PENDING_PAYMENT: "PENDING_PAYMENT",
  PENDING_ASSESSMENT: "PENDING_ASSESSMENT",
  ACTIVE: "ACTIVE",
  /** All 21 lessons submitted; final assessment may still be pending. */
  LESSONS_COMPLETED: "LESSONS_COMPLETED",
  /** Completion goal met — full deposit refunded. */
  CHALLENGE_COMPLETE: "CHALLENGE_COMPLETE",
  /** Completion goal missed — deposit forfeited, no refund. */
  CHALLENGE_INCOMPLETE: "CHALLENGE_INCOMPLETE",
  /**
   * User quit before finishing. The server only refunds when the challenge
   * had not started yet (PENDING_PAYMENT / PENDING_ASSESSMENT); quitting
   * from ACTIVE forfeits the deposit.
   */
  CANCELLED: "CANCELLED",
};

/** The screen that owns each status. Shared by the router and the guards. */
export const CHALLENGE_STATUS_ROUTE = {
  [CHALLENGE_STATUS.NONE]: "enroll",
  [CHALLENGE_STATUS.PENDING_PAYMENT]: "enroll",
  [CHALLENGE_STATUS.PENDING_ASSESSMENT]: "pending-assessment",
  [CHALLENGE_STATUS.ACTIVE]: "active",
  [CHALLENGE_STATUS.LESSONS_COMPLETED]: "lessons-completed",
  /*
   * A successfully closed challenge sends the user back to enrol, not to the
   * result screen. The server only reaches CHALLENGE_COMPLETE once the final
   * assessment is in *or* the grace window has lapsed, and it accepts a fresh
   * enrollment from that point — so there is nothing left to do here except
   * start again, and the result screen would be a dead end that hides it. The
   * result screen is still shown once, pushed straight after the assessment;
   * after that the run lives in the Previous Challenges tab.
   */
  // Resolved dynamically by the router — see isWithinPostCompletionWindow.
  [CHALLENGE_STATUS.CHALLENGE_COMPLETE]: "lessons-completed",
  // A failed challenge keeps its result screen: the forfeited deposit needs
  // explaining, and that screen offers a fresh start rather than ending there.
  [CHALLENGE_STATUS.CHALLENGE_INCOMPLETE]: "result",
  // A quitter gets the enrol screen, not a result screen: there is no result
  // to show and starting fresh is the only move left to them.
  [CHALLENGE_STATUS.CANCELLED]: "enroll",
};

export const CHALLENGE_DAY_STATUS = {
  PENDING: "pending",
  TODAY: "today",
  SUBMITTED: "submitted",
  MISSED: "missed",
  FROZEN: "frozen",
};

/**
 * Currency is the payer's choice, not something we infer — someone in India
 * may still want to pay in USD, and timezone/IP is a poor proxy for that.
 * This is only the option the selector starts on.
 */
export const DEFAULT_CHALLENGE_CURRENCY = "INR";

/** Selectable currency options, in the order they're offered. */
export const CHALLENGE_CURRENCY_OPTIONS = Object.values(CHALLENGE_PRICING);

/**
 * @param {string} currency - ISO code, e.g. "INR" or "USD".
 * @returns {{amount: number, currency: string, symbol: string}}
 */
export const getChallengeDeposit = (currency) =>
  CHALLENGE_CURRENCY_OPTIONS.find((option) => option.currency === currency) ||
  CHALLENGE_PRICING.IN;

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Parses a date the challenge API sent ("2026-10-06") into a local Date.
 *
 * `new Date("2026-10-06")` is read as UTC midnight, which renders as Oct 5
 * for anyone west of Greenwich — and on the 1st of a month that silently
 * prints the *previous* month on a completion badge. Splitting the string
 * keeps the calendar date the server actually meant.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
const parseChallengeDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const [year, month, day] = String(value).split("T")[0].split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

/**
 * Formats a challenge date as a month and year, e.g. "Oct 2026".
 *
 * @param {string|Date|null|undefined} value
 * @param {{long?: boolean}} [options] - `long: true` gives "October 2026".
 * @returns {string} empty string when the date is missing or unparseable.
 */
export const formatChallengeMonth = (value, { long = false } = {}) => {
  const date = parseChallengeDate(value);
  if (!date) return "";
  const names = long ? MONTHS_LONG : MONTHS_SHORT;
  return `${names[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * The date the 21st and final practice day falls on.
 *
 * All 21 days are consecutive and mandatory, so the enrollment's start date
 * fixes the completion date exactly. Derived rather than read off a
 * submission timestamp, which carries the server's clock rather than the
 * user's and can land a midnight finish in the wrong month.
 *
 * @param {string|Date|null|undefined} startDate
 * @returns {Date|null}
 */
export const getChallengeCompletionDate = (startDate) => {
  const start = parseChallengeDate(startDate);
  if (!start) return null;
  const end = new Date(start);
  end.setDate(end.getDate() + CHALLENGE_RULES.TOTAL_DAYS - 1);
  return end;
};

/**
 * Last date on which the optional final assessment still counts: the grace
 * window opens on the final lesson day and runs for FINAL_ASSESSMENT_GRACE_DAYS
 * after it. Mirrors `final_assessment_deadline` in the backend engine.
 *
 * @param {string|Date|null|undefined} startDate
 * @returns {Date|null}
 */
export const getFinalAssessmentDeadline = (startDate) => {
  const end = getChallengeCompletionDate(startDate);
  if (!end) return null;
  const deadline = new Date(end);
  deadline.setDate(deadline.getDate() + CHALLENGE_RULES.FINAL_ASSESSMENT_GRACE_DAYS);
  return deadline;
};

/**
 * Whether the final assessment can still be taken for an enrollment.
 *
 * The server is the authority — it closes the challenge out the moment the
 * window lapses — but a tab left open across midnight keeps serving a stale
 * status, and offering a button that is guaranteed to fail is worse than
 * offering none. Unknown start date means "assume open" so a missing field
 * never hides the primary action.
 *
 * @param {string|Date|null|undefined} startDate
 * @param {Date} [today]
 * @returns {boolean}
 */
export const isFinalAssessmentWindowOpen = (startDate, today = new Date()) => {
  const deadline = getFinalAssessmentDeadline(startDate);
  if (!deadline) return true;
  const todayAtMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return todayAtMidnight <= deadline;
};

/**
 * Whether a finished challenge should still show its completion screen.
 *
 * The server closes the enrollment out as soon as the final assessment is
 * taken or the grace window lapses, but the user is not done with the screen
 * at that moment — the refund is still in flight. This keeps the completion
 * message (and the final-assessment prompt behind it) for
 * POST_COMPLETION_DAYS after the last lesson day, after which a new
 * challenge is offered instead.
 *
 * Unknown start date means "still in the window", so a missing field never
 * silently retires the screen that explains the refund.
 *
 * @param {string|Date|null|undefined} startDate
 * @param {Date} [today]
 * @returns {boolean}
 */
export const isWithinPostCompletionWindow = (startDate, today = new Date()) => {
  const end = getChallengeCompletionDate(startDate);
  if (!end) return true;
  const until = new Date(end);
  until.setDate(until.getDate() + CHALLENGE_RULES.POST_COMPLETION_DAYS);
  const todayAtMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return todayAtMidnight <= until;
};

/**
 * Placeholder practice passages, one per day. The real personalized plan
 * (per-user, generated from the initial assessment's top5_error_patterns —
 * see design doc §4.3) doesn't exist yet; these are generic sentences with
 * varied phonetic content, standing in until Phase 2 wires real lesson data.
 */
export const CHALLENGE_PASSAGES = [
  "The quick brown fox jumps over the lazy dog near the riverbank.",
  "She sells seashells by the seashore every summer morning.",
  "Thirty-three thieves thought they thrilled the throne throughout Thursday.",
  "Peter Piper picked a peck of pickled peppers from the pantry shelf.",
  "The weather forecast predicted heavy thunderstorms across the western valley.",
  "Rural jurors rarely worry about irregular grammar during a trial.",
  "A proper cup of coffee from a proper copper coffee pot.",
  "The chef prepared a delicious dish with fresh herbs and spices.",
  "Six sleek swans swam swiftly southward, skimming the silver stream.",
  "How much wood would a woodchuck chuck if a woodchuck could chuck wood?",
  "The entrepreneur negotiated a lucrative contract with the pharmaceutical company.",
  "Fred fed Ted bread, and Ted fed Fred bread in return.",
  "Unique New York, unique New York, you know you need unique New York.",
  "The auditorium echoed with enthusiastic applause after the orchestra's performance.",
  "Betty Botter bought some butter, but the butter Betty bought was bitter.",
  "The astronaut described the breathtaking view of Earth from orbit.",
  "Red lorries and yellow lorries travel along the motorway at rush hour.",
  "The librarian recommended a fascinating biography about a nineteenth-century explorer.",
  "Can you can a can as a canner can can a can?",
  "The negotiation required careful diplomacy and mutual understanding between both parties.",
  "Congratulations on completing all twenty-one days of your speaking challenge!",
];

/**
 * @param {number} dayIndex - 1-based challenge day.
 * @returns {string}
 */
export const getChallengePassage = (dayIndex) =>
  CHALLENGE_PASSAGES[(dayIndex - 1) % CHALLENGE_PASSAGES.length];
